import { useEffect, useRef, useState } from "react";
import {
  FirmwareRelease,
  FirmwareStatus,
  deleteFirmware,
  formatBytes,
  listFirmware,
  setFirmwareStatus,
  uploadFirmware,
} from "../../../Services/Admin/Firmware";
import "./FirmwarePanel.css";

/**
 * Uppladdning och utrullning av firmware.
 *
 * Att lägga upp en binär gör ingenting i sig — den hamnar i draft. Först
 * när någon flyttar den till canary eller stable kan en sensor hämta
 * den, och bara sensorer som separat fått OTA påslaget i sensorpanelen.
 * Två spärrar, för att ett felaktigt bygge inte ska kunna tysta varje
 * låda som står ute hos en kund.
 */

const STATUS_HELP: Record<FirmwareStatus, string> = {
  draft: "Not served to anyone.",
  canary: "Only sensors pointed at this exact version.",
  stable: "Every sensor with OTA enabled.",
  archived: "No longer served. The row stays for the record.",
};

export default function FirmwarePanel() {
  const [releases, setReleases] = useState<FirmwareRelease[]>([]);
  const [appPartitionBytes, setAppPartitionBytes] = useState<number | null>(null);
  const [maxAttempts, setMaxAttempts] = useState<number | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const [version, setVersion] = useState("");
  const [notes, setNotes] = useState("");
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef<HTMLInputElement>(null);

  const load = () => {
    setLoading(true);
    setError(null);

    listFirmware()
      .then((data) => {
        setReleases(data.items);
        setAppPartitionBytes(data.appPartitionBytes);
        setMaxAttempts(data.maxOtaAttempts);
      })
      .catch((e) => setError(e?.message ?? "Could not load releases"))
      .finally(() => setLoading(false));
  };

  useEffect(load, []);

  const onUpload = async (event: React.FormEvent) => {
    event.preventDefault();
    setError(null);
    setNotice(null);

    const file = fileRef.current?.files?.[0];
    if (!file) {
      setError("Pick a firmware.bin to upload.");
      return;
    }

    setUploading(true);
    try {
      const res = await uploadFirmware({ version: version.trim(), notes, file });
      setReleases((prev) => [res.release, ...prev]);
      setNotice(
        `${res.release.version} uploaded as draft. It is not served until you move it to canary or stable.`
      );
      setVersion("");
      setNotes("");
      if (fileRef.current) fileRef.current.value = "";
    } catch (e: any) {
      setError(e?.message ?? "The upload failed");
    } finally {
      setUploading(false);
    }
  };

  const onStatus = async (release: FirmwareRelease, status: FirmwareStatus) => {
    setError(null);
    setNotice(null);

    try {
      await setFirmwareStatus(release.id, status);
      // Stable arkiverar den förra stabila på servern, så listan hämtas
      // om i stället för att gissa vad som hände med de andra raderna.
      load();
    } catch (e: any) {
      setError(e?.message ?? "Could not change the status");
    }
  };

  const onDelete = async (release: FirmwareRelease) => {
    const confirmed = window.confirm(
      `Delete firmware ${release.version}?\n\nThe binary and release record will be removed. This cannot be undone.`
    );
    if (!confirmed) return;
    setError(null);
    setNotice(null);

    try {
      await deleteFirmware(release.id);
      setReleases((prev) => prev.filter((r) => r.id !== release.id));
    } catch (e: any) {
      setError(e?.message ?? "Could not delete the release");
    }
  };

  return (
    <div className="admin-panel">
      <form className="firmware-upload" onSubmit={onUpload}>
        <div className="firmware-row">
          <label>
            Version
            <input
              value={version}
              onChange={(e) => setVersion(e.target.value)}
              placeholder="1.2.0"
              required
            />
          </label>

          <label className="firmware-file">
            firmware.bin
            <input type="file" accept=".bin" ref={fileRef} required />
          </label>
        </div>

        <label>
          Notes
          <input
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="What changed?"
          />
        </label>

        <button type="submit" className="admin-btn--primary" disabled={uploading}>
          {uploading ? "Uploading…" : "Upload as draft"}
        </button>

        <p className="firmware-dim">
          The version must match FIRMWARE_VERSION in src/version.h. Otherwise the
          server never sees that the update landed, and offers the same binary again
          {maxAttempts ? ` up to ${maxAttempts} times` : ""}.
          {appPartitionBytes
            ? ` The binary may be at most ${formatBytes(appPartitionBytes)}.`
            : ""}
        </p>
      </form>

      {error && <div className="admin-error">{error}</div>}
      {notice && <div className="admin-notice">{notice}</div>}

      <div className="admin-toolbar">
        <button onClick={load} disabled={loading}>
          {loading ? "Loading…" : "Refresh"}
        </button>
      </div>

      {!loading && releases.length === 0 && (
        <p className="admin-empty">No releases uploaded yet.</p>
      )}

      {releases.map((release) => (
        <div className="firmware-card" key={release.id}>
          <div className="firmware-head">
            <strong>{release.version}</strong>
            <span className={`firmware-status firmware-status--${release.status}`}>
              {release.status}
            </span>
            <span className="firmware-dim">
              {formatBytes(release.sizeBytes)} ·{" "}
              {new Date(release.createdAt).toLocaleString()}
            </span>
          </div>

          {release.notes && <p className="firmware-notes">{release.notes}</p>}

          <p className="firmware-dim firmware-hash" title={release.sha256}>
            sha256 {release.sha256.slice(0, 16)}…
          </p>

          <p className="firmware-dim">{STATUS_HELP[release.status]}</p>

          {release.pinnedSensors > 0 && (
            <p className="firmware-dim">
              {release.pinnedSensors} sensor(s) pinned to this release.
            </p>
          )}

          <div className="firmware-actions">
            {release.status !== "canary" && (
              <button onClick={() => onStatus(release, "canary")}>
                Make canary
              </button>
            )}

            {release.status !== "stable" && (
              <button onClick={() => onStatus(release, "stable")}>
                Make stable
              </button>
            )}

            {release.status !== "archived" && (
              <button onClick={() => onStatus(release, "archived")}>
                Archive
              </button>
            )}

            {/* Servern vägrar radera en release som sensorer är pekade
                mot, så knappen döljs hellre än att erbjuda ett fel. */}
            {release.pinnedSensors === 0 && (
              <button
                className="admin-btn--danger"
                onClick={() => onDelete(release)}
              >
                Delete
              </button>
            )}
          </div>
        </div>
      ))}
    </div>
  );
}
